import { useEnrichOffersWithChannelIds } from '@hooks/useEnrichOffersWithChannelIds';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import {
	Box,
	CircularProgress,
	Grid,
	Pagination,
	Typography,
} from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSpecialistOffersStore } from '../services';
import { getOffers } from '../services/services';
import { OfferStatus } from '../types';
import { OfferCard } from './OfferCard';

interface OffersListProps {
	status?: OfferStatus;
	onStatusChange?: (status: OfferStatus) => void;
}

export const OffersList: React.FC<OffersListProps> = ({ status = 'active', onStatusChange }) => {
	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();

	const { offers, pagination, isLoading, error, setOffers, setIsLoading, setError } =
		useSpecialistOffersStore();
	const { enrichOffersWithChannelIds } = useEnrichOffersWithChannelIds();

	const [page, setPage] = useState(1);
	const isMountedRef = useRef(true);
	const isFirstLoadRef = useRef(true);

	// Fetch offers when component mounts or status/page changes
	useEffect(() => {
		// Set up the mounted ref
		isMountedRef.current = true;
		
		const fetchOffersData = async () => {
			if (!isMountedRef.current) return;
			
			try {
				console.log(`[OffersList] Fetching offers with status=${status}, page=${page}`);
				setIsLoading(true);
				
				const offersResponse = await getOffers(status, page);
				console.log(`[OffersList] Received ${offersResponse.data.length} offers`);
				
				if (!isMountedRef.current) return;
				
				setOffers(offersResponse);

				// Once offers are loaded and set in the store, enrich them with channel IDs
				if (offersResponse.data.length > 0) {
					// Prepare offers for enrichment by ensuring they have the required properties
					const preparedOffers = offersResponse.data.map((offer) => ({
						...offer,
						farmer_id: offer.farmer?.id,
						request_id: offer.request_id,
					}));

					console.log(`[OffersList] Enriching ${preparedOffers.length} offers with channel IDs`);
					
					// Use our hook to fetch channel IDs
					await enrichOffersWithChannelIds(preparedOffers, (enrichedOffers) => {
						if (!isMountedRef.current) return;
						
						console.log(`[OffersList] Successfully enriched ${enrichedOffers.length} offers`);
						
						// Update the store with the enriched offers
						setOffers({
							...offersResponse,
							data: enrichedOffers,
						});
					});
				}
			} catch (error: any) {
				console.error('[OffersList] Error fetching offers:', error);
				
				if (!isMountedRef.current) return;
				
				setError(error instanceof Error ? error.message : 'Failed to fetch offers');
			} finally {
				if (isMountedRef.current) {
					console.log('[OffersList] Setting loading state to false');
					setIsLoading(false);
					isFirstLoadRef.current = false;
				}
			}
		};

		fetchOffersData();

		// Cleanup function
		return () => {
			console.log('[OffersList] Component unmounting');
			isMountedRef.current = false;
		};
	}, [status, page, setOffers, setIsLoading, setError, enrichOffersWithChannelIds]);

	const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
		console.log(`[OffersList] Page changed to ${value}`);
		setPage(value);
	};

	const handleSendOffer = (offerId: string) => {
		navigateWithLanguage(`/specialist/seasonal-onetime/offers/${offerId}`);
	};

	const handleEditOffer = (offerId: string) => {
		navigateWithLanguage(`/specialist/seasonal-onetime/offers/${offerId}`);
	};

	const handleChat = (request_id: string) => {
		// Find the offer with this ID
		const offer = offers.find((o) => o.request_id === request_id);
		if (offer && 'channelId' in offer && offer.channelId) {
			navigateWithLanguage(`/specialist/chats?channelId=${offer.channelId}`);
		} else {
			console.log('No channel ID available for this offer');
		}
	};

	const handleViewDetails = (offerId: string) => {
		navigateWithLanguage(`/specialist/seasonal-onetime/offers/${offerId}`);
	};

	// Show loading state
	if (isLoading) {
		return (
			<Box className="flex flex-col justify-center items-center h-64">
				<CircularProgress size={40} />
				<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
					{t('loadingOffers', 'Loading offers...')}
				</Typography>
			</Box>
		);
	}

	// Show error message if there's an error
	if (error) {
		return (
			<Box className="flex flex-col justify-center items-center h-64">
				<Typography color="error">{error}</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
					{t('tryAgainLater', 'Please try again later.')}
				</Typography>
			</Box>
		);
	}

	// Show empty state if there are no offers
	if (!isLoading && offers.length === 0) {
		return (
			<Box className="flex flex-col items-center justify-center h-64">
				<Typography variant="body1" className="text-gray-600">
					{status === 'active'
						? t('noOngoingOffers', "You don't have any ongoing offers.")
						: status === 'archived'
							? t('noArchivedOffers', "You don't have any archived offers.")
							: t('noCompletedOffersYet', "You don't have any completed offers.")}
				</Typography>
			</Box>
		);
	}

	return (
		<Box className="py-4">
			<Box className="flex justify-between items-center mb-6">
				<Typography variant="h5" className="font-medium">
					{status === 'active'
						? t('ongoingOffers', 'Ongoing Offers')
						: status === 'archived'
							? t('archivedOffers', 'Archived Offers')
							: t('completedOffers', 'Completed Offers')}
				</Typography>
			</Box>

			<Grid container spacing={3}>
				{offers.map((offer) => (
					<Grid item xs={12} sm={6} md={6} key={offer.id}>
						<OfferCard
							offer={offer}
							onSendOffer={handleSendOffer}
							onEditOffer={handleEditOffer}
							onChat={handleChat}
							onViewDetails={handleViewDetails}
							status={status}
						/>
					</Grid>
				))}
			</Grid>

			{pagination && pagination.total_pages > 1 && (
				<Box className="flex justify-center mt-6">
					<Pagination
						count={pagination.total_pages}
						page={page}
						onChange={handlePageChange}
						color="primary"
						shape="rounded"
					/>
				</Box>
			)}
		</Box>
	);
};
